import { useEffect, useMemo, useRef, useState } from 'react'
import { ExitButton } from '../components/ExitButton'
import type { MouseEvent } from 'react'
import { api, type Universe } from '../api'
import { radius, theme, TAP_MIN } from '../theme'
import { Button, Page } from '../components/ui'
import { ErrorScreen, Loading } from '../components/States'
import { Mascot } from '../components/Mascot'
import { Trace } from '../lib/trace'
import { say } from '../lib/speech'
import { sfx } from '../lib/sound'

/**
 * The word universe: every word a child has met, as a star.
 *
 * The word map shows one neighbourhood at a time, on purpose — a thousand
 * labelled blobs is a grey cloud. This is the other answer: the whole sky,
 * no labels until you tap, one galaxy per level. Charles's 1,075 words are
 * 1,075 stars.
 *
 * **What the light means.** A fully grown word is a warm gold star with a
 * glow. A word still growing is a small pale-blue one. A word met in the last
 * week pulses gently. Nothing here is decoration: brightness is learning,
 * and the pulse is recency — the same two facts the garden and the map show,
 * drawn as a sky.
 *
 * **It only grows** (D-048). A star, once lit, never goes out; a grown word
 * stays gold even if it was later missed.
 *
 * **Tapping.** A star is far too small to hit with a finger, so a tap finds
 * the nearest star to where it landed. The tapped word is spoken, its meaning
 * is fetched from the word map, and the words it is joined to are drawn as a
 * constellation. There is also a search box, which is how anyone not using a
 * pointer reaches a star.
 */

type Placed = { w: string; g: number; b: number | null; d: number | null; x: number; y: number }
type Galaxy = { band: number | null; name: string; x: number; y: number; r: number; count: number; hue: string }

const HUES = ['#7C5CFF', '#FF6FB5', '#4FC3F7', '#5EEAD4', '#FFB347', '#A3E635',
              '#F472B6', '#60A5FA', '#FBBF24', '#34D399', '#C084FC', '#F87171', '#22D3EE']
const GOLDEN = Math.PI * (3 - Math.sqrt(5))

/** A fixed random sequence, so the sky is the same every visit. */
function seeded(seed: number) {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 2 ** 32
  }
}

function layout(data: Universe): { stars: Placed[]; galaxies: Galaxy[]; box: [number, number, number, number] } {
  const groups = new Map<number | null, Universe['stars']>()
  for (const s of data.stars) {
    const key = s.b ?? null
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(s)
  }
  const keys = [...groups.keys()].sort((a, b) => (a ?? 99) - (b ?? 99))
  const names = new Map(data.galaxies.map((g) => [g.band, g.name]))

  const galaxies: Galaxy[] = []
  const stars: Placed[] = []
  let t = 0
  keys.forEach((band, k) => {
    // Grown stars first, so they gather at the bright heart of their galaxy.
    const members = [...groups.get(band)!].sort((a, b) => b.g - a.g || a.w.localeCompare(b.w))
    const r = 26 + 9.5 * Math.sqrt(members.length)
    // Walk out along a spiral and take the first spot that touches nothing
    // already placed. The first version pushed each galaxy out by the full
    // width of the last, which left a sky so empty that every galaxy was a
    // smudge and every name unreadable.
    let cx = 0
    let cy = 0
    if (k > 0) {
      for (;;) {
        t += 0.04
        cx = Math.cos(t) * 9 * t
        cy = Math.sin(t) * 9 * t
        const clear = galaxies.every((g) => Math.hypot(g.x - cx, g.y - cy) >= g.r + r + 52)
        if (clear) break
      }
    }
    const hue = HUES[(band ?? 12) % HUES.length]!
    galaxies.push({ band, name: band === null ? 'Unsorted' : names.get(band) ?? `Level ${band}`,
                    x: cx, y: cy, r, count: members.length, hue })
    const step = (r - 12) / Math.sqrt(Math.max(members.length, 1))
    members.forEach((s, i) => {
      const rr = step * Math.sqrt(i + 0.5)
      const th = i * GOLDEN + k
      stars.push({ ...s, x: cx + Math.cos(th) * rr, y: cy + Math.sin(th) * rr })
    })
  })

  const pad = 60
  const minX = Math.min(...galaxies.map((g) => g.x - g.r)) - pad
  const maxX = Math.max(...galaxies.map((g) => g.x + g.r)) + pad
  const minY = Math.min(...galaxies.map((g) => g.y - g.r)) - pad
  const maxY = Math.max(...galaxies.map((g) => g.y + g.r)) + pad + 50
  return { stars, galaxies, box: [minX, minY, maxX - minX, maxY - minY] }
}

let styled = false
function ensureStyle(): void {
  if (styled || typeof document === 'undefined') return
  styled = true
  const el = document.createElement('style')
  el.textContent = `
    @keyframes ws-pulse { 0%,100% { opacity: 1 } 50% { opacity: 0.35 } }
    .ws-recent { animation: ws-pulse 2.2s ease-in-out infinite; }
    @keyframes ws-sky { 0%,100% { opacity: 0.25 } 50% { opacity: 0.9 } }
    .ws-sky { animation: ws-sky 4s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) { .ws-recent, .ws-sky { animation: none } }
  `
  document.head.appendChild(el)
}

export function WordUniverse({
  learnerId, onHome, onGarden, on3D,
}: { learnerId: string; onHome: () => void; onGarden: () => void; on3D?: () => void }) {
  ensureStyle()
  const [data, setData] = useState<Universe | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [zoom, setZoom] = useState(1)
  const [picked, setPicked] = useState<Placed | null>(null)
  const [info, setInfo] = useState<{ definition: string | null; neighbours: string[] } | null>(null)
  const [query, setQuery] = useState('')
  const svgRef = useRef<SVGSVGElement | null>(null)

  const load = () => {
    setError(null)
    api.universe(learnerId).then(setData).catch(setError)
  }
  useEffect(load, [learnerId])

  const sky = useMemo(() => (data ? layout(data) : null), [data])
  const byWord = useMemo(() => {
    const m = new Map<string, Placed>()
    for (const s of sky?.stars ?? []) m.set(s.w.toLowerCase(), s)
    return m
  }, [sky])

  const dust = useMemo(() => {
    if (!sky) return []
    const [x, y, w, h] = sky.box
    const rnd = seeded(7)
    return Array.from({ length: 160 }, () => ({
      x: x + rnd() * w, y: y + rnd() * h, r: 0.6 + rnd() * 1.3, delay: rnd() * 4,
    }))
  }, [sky])

  const pick = (star: Placed | null) => {
    setPicked(star)
    setInfo(null)
    if (!star) return
    sfx.tap()
    say(star.w)
    api.wordMap(learnerId, star.w, null, 12)
      .then((map) => {
        const me = map.nodes.find((n) => n.word.toLowerCase() === star.w.toLowerCase())
        const id = me?.id
        const neighbours = map.edges
          .filter((e) => e.a === id || e.b === id)
          .map((e) => (e.a === id ? e.b : e.a))
          .map((nid) => map.nodes.find((n) => n.id === nid)?.word)
          .filter((w): w is string => Boolean(w))
        setInfo({ definition: me?.definition ?? null, neighbours })
      })
      .catch(() => setInfo({ definition: null, neighbours: [] }))
  }

  /** A star is too small for a finger: take the nearest one to the tap. */
  const onSkyClick = (e: MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current
    if (!svg || !sky) return
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY
    const ctm = svg.getScreenCTM()
    if (!ctm) return
    const p = pt.matrixTransform(ctm.inverse())
    let best: Placed | null = null
    let bestD = Infinity
    for (const s of sky.stars) {
      const d = (s.x - p.x) ** 2 + (s.y - p.y) ** 2
      if (d < bestD) { bestD = d; best = s }
    }
    // Within a reach that grows as you zoom out, so tapping empty sky far
    // from any galaxy clears the selection instead of grabbing a stranger.
    pick(bestD < (28 / zoom) ** 2 ? best : null)
  }

  const find = (text: string) => {
    setQuery(text)
    const hit = byWord.get(text.trim().toLowerCase())
    if (hit) pick(hit)
  }

  if (error) return <ErrorScreen error={error} onRetry={load} onHome={onHome} />
  if (!data || !sky) return <Loading what="Lighting up the stars" />

  const [vx, vy, vw, vh] = sky.box
  // Labels sized to the sky, so a name reads at about 15px however many
  // galaxies there are.
  const label = Math.max(15, vw / 46)
  const lines = picked && info
    ? info.neighbours.map((w) => byWord.get(w.toLowerCase())).filter((s): s is Placed => Boolean(s))
    : []

  return (
    <Page width={760}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <h1 style={{ margin: '4px 0 2px', fontSize: 30, color: theme.text }}>Word universe</h1>
        {on3D ? <Button tone="quiet" onClick={on3D}>🪐 3D view</Button> : null}
      </div>
      <p style={{ margin: '0 0 12px', fontSize: 16, color: theme.muted }}>
        <Trace id="calc.counted from T7 rows">{data.total.toLocaleString()} stars</Trace>
        {' · '}
        <Trace id="calc.counted from T7.grown_at">{data.grown.toLocaleString()} shining bright</Trace>
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={query}
          onChange={(e) => find(e.target.value)}
          placeholder="Find a star — type a word"
          aria-label="Find a word in your universe"
          style={{
            flex: '1 1 200px', minHeight: TAP_MIN, borderRadius: radius.pill,
            border: `1.5px solid ${theme.border}`, padding: '0 16px', fontSize: 16,
            background: theme.card, color: theme.text,
          }}
        />
        {[['−', 0.5], ['+', 2]].map(([label, f]) => (
          <button key={label as string} type="button"
            aria-label={label === '+' ? 'Zoom in' : 'Zoom out'}
            onClick={() => setZoom((z) => Math.min(4, Math.max(1, z * (f as number))))}
            style={{
              width: TAP_MIN, height: TAP_MIN, borderRadius: radius.pill, fontSize: 22,
              fontWeight: 700, border: `1.5px solid ${theme.border}`, background: theme.card,
              color: theme.text, cursor: 'pointer',
            }}>{label}</button>
        ))}
      </div>

      <div style={{
        position: 'relative', borderRadius: 22, overflow: 'auto',
        background: 'radial-gradient(ellipse at 30% 20%, #2B1B4F 0%, #121734 55%, #070A1C 100%)',
        border: '3px solid #2E2140', maxHeight: '70vh',
        boxShadow: '0 8px 24px rgba(20,10,50,0.35)',
      }}>
        <svg
          ref={svgRef}
          viewBox={`${vx} ${vy} ${vw} ${vh}`}
          role="img"
          aria-label={`${data.total} words as stars, ${data.grown} of them fully grown`}
          onClick={onSkyClick}
          style={{ display: 'block', width: `${zoom * 100}%`, height: 'auto', cursor: 'pointer' }}
        >
          <defs>
            <filter id="ws-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.6" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            {sky.galaxies.map((g, i) => (
              <radialGradient key={i} id={`ws-neb-${i}`}>
                <stop offset="0" stopColor={g.hue} stopOpacity="0.45" />
                <stop offset="0.6" stopColor={g.hue} stopOpacity="0.12" />
                <stop offset="1" stopColor={g.hue} stopOpacity="0" />
              </radialGradient>
            ))}
          </defs>

          {/* far-off dust */}
          {dust.map((d, i) => (
            <circle key={`d${i}`} className="ws-sky" cx={d.x} cy={d.y} r={d.r} fill="#FFFFFF"
                    style={{ animationDelay: `${d.delay}s` }} />
          ))}

          {/* galaxies */}
          {sky.galaxies.map((g, i) => (
            <g key={`g${i}`}>
              <circle cx={g.x} cy={g.y} r={g.r * 1.15} fill={`url(#ws-neb-${i})`} />
              <text x={g.x} y={g.y + g.r + label * 1.3} textAnchor="middle" fill="#E8E2FF"
                    fontFamily="'Fredoka', sans-serif" fontSize={label} fontWeight="600" opacity="0.92">
                {g.name}
              </text>
              <text x={g.x} y={g.y + g.r + label * 2.35} textAnchor="middle" fill="#A9A2D6"
                    fontFamily="'Nunito', sans-serif" fontSize={label * 0.75}>
                {g.count} {g.count === 1 ? 'star' : 'stars'}
              </text>
            </g>
          ))}

          {/* stars still growing */}
          {sky.stars.filter((s) => !s.g).map((s, i) => (
            <circle key={`s${i}`} className={s.d !== null && s.d <= 7 ? 'ws-recent' : undefined}
                    cx={s.x} cy={s.y} r={2.4} fill="#CFE3FF" opacity={0.85} />
          ))}
          {/* fully grown: gold, and glowing */}
          <g filter="url(#ws-glow)">
            {sky.stars.filter((s) => s.g).map((s, i) => (
              <circle key={`G${i}`} className={s.d !== null && s.d <= 7 ? 'ws-recent' : undefined}
                      cx={s.x} cy={s.y} r={3.6} fill="#FFE29A" />
            ))}
          </g>

          {/* the tapped star's constellation */}
          {picked ? (
            <g>
              {lines.map((n, i) => (
                <line key={`l${i}`} x1={picked.x} y1={picked.y} x2={n.x} y2={n.y}
                      stroke="#FFFFFF" strokeOpacity="0.55" strokeWidth={1.3 / zoom}
                      strokeDasharray={`${4 / zoom} ${3 / zoom}`} />
              ))}
              {lines.map((n, i) => (
                <text key={`n${i}`} x={n.x} y={n.y - label * 0.5} textAnchor="middle" fill="#D9D3FF"
                      fontSize={(label * 0.8) / Math.sqrt(zoom)} fontFamily="'Nunito', sans-serif">{n.w}</text>
              ))}
              <circle cx={picked.x} cy={picked.y} r={9 / Math.sqrt(zoom)} fill="none"
                      stroke="#FFFFFF" strokeWidth={1.6 / zoom} />
              <text x={picked.x} y={picked.y - label * 0.8} textAnchor="middle" fill="#FFFFFF"
                    fontSize={(label * 1.1) / Math.sqrt(zoom)} fontWeight="700"
                    fontFamily="'Fredoka', sans-serif">{picked.w}</text>
            </g>
          ) : null}
        </svg>

        {/* Sprout, floating by in a bubble */}
        <div aria-hidden="true" style={{
          position: 'sticky', left: 12, bottom: 12, width: 74, height: 74, marginTop: -86,
          borderRadius: 999, background: 'radial-gradient(circle at 35% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0.08) 60%)',
          border: '2px solid rgba(255,255,255,0.45)', display: 'grid', placeItems: 'center',
          pointerEvents: 'none',
        }}>
          <Mascot size={46} mood="wonder" label="" />
        </div>
      </div>

      {/* --------------------------------------------- the tapped word */}
      <div role="status" style={{ minHeight: 92, marginTop: 12 }}>
        {picked ? (
          <div style={{
            background: theme.card, border: `1px solid ${theme.borderWarm}`,
            borderRadius: radius.xl, padding: 16,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontFamily: "'Fredoka', sans-serif", fontSize: 28, fontWeight: 600, color: theme.text }}>
                {picked.w}
              </span>
              <button type="button" onClick={() => say(picked.w)} aria-label={`Say ${picked.w}`} style={{
                minWidth: TAP_MIN, minHeight: TAP_MIN, borderRadius: radius.pill, fontSize: 18,
                border: `1.5px solid ${theme.border}`, background: theme.card, cursor: 'pointer',
              }}>🔊</button>
              <span style={{
                fontSize: 14, fontWeight: 700, borderRadius: radius.pill, padding: '4px 10px',
                background: picked.g ? '#FFF4D6' : theme.blueFaint,
                color: picked.g ? '#8A5A00' : theme.blueDark,
              }}>
                {picked.g ? '⭐ Fully grown' : '🌱 Still growing'}
              </span>
            </div>
            <p style={{ margin: '8px 0 4px', fontSize: 16, color: theme.text2, lineHeight: 1.5 }}>
              {info ? (info.definition ?? 'No meaning saved for this one yet.') : 'Looking it up…'}
            </p>
            <p style={{ margin: 0, fontSize: 14, color: theme.muted }}>
              {picked.d === null ? 'Not met in practice yet.'
                : picked.d === 0 ? 'Met today.'
                : `Last met ${picked.d} day${picked.d === 1 ? '' : 's'} ago.`}
              {info && info.neighbours.length
                ? ` Joined to ${info.neighbours.slice(0, 4).join(', ')}${info.neighbours.length > 4 ? '…' : ''}.`
                : ''}
            </p>
          </div>
        ) : (
          <p style={{ textAlign: 'center', color: theme.faint, fontSize: 14, margin: '6px 0 0' }}>
            Tap any star to hear its word. Gold ones are fully grown; the twinkling ones you met this week.
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
        <Button tone="child" onClick={onGarden}>See my garden</Button>
        <ExitButton onExit={onHome} />
      </div>
    </Page>
  )
}
